/** @format */

import apis from "../../utils/apis";

// Max attempts for all skill tree API calls (initial try + retries)
const SKILL_TREE_MAX_ATTEMPTS = 3;

// Decide if an error should trigger a retry (504, timeouts / API Gateway timeout style errors)
const isGatewayTimeoutOrRetryableError = (error) => {
  const status = error?.response?.status;
  if (status === 504) {
    return true;
  }

  // Common timeout / aborted request codes
  const code = error?.code;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") {
    return true;
  }

  const message =
    (error?.response?.data?.body?.message ||
      error?.response?.data?.message ||
      error?.message ||
      ""
    ).toLowerCase();

  if (
    message.includes("gateway timeout") ||
    message.includes("api gateway timeout") ||
    message.includes("timeout") ||
    message.includes("timed out")
  ) {
    return true;
  }

  return false;
};

// Wrap a skill tree API call in retry logic
const callSkillTreeWithRetry = async (requestFn) => {
  let attempt = 1;
  let lastError;

  while (attempt <= SKILL_TREE_MAX_ATTEMPTS) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // If not retryable or we've used all attempts, rethrow
      if (!isGatewayTimeoutOrRetryableError(error) || attempt === SKILL_TREE_MAX_ATTEMPTS) {
        throw error;
      }

      // Simple incremental backoff: 500ms, 1000ms, 1500ms, ...
      const delayMs = 500 * attempt;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      attempt += 1;
    }
  }

  // Safety fallback
  throw lastError;
};

/**
 * Get Level 1+2 data (Fast initial load)
 * @param {string} skillName - The main skill/technology name
 * @param {string} resultId - MongoDB ObjectId from tech_relevance_result collection
 * @param {string} model - AI model to use: "gpt", "gemini", or "claude" (default: "gpt")
 * @returns {Promise} API response with Level 1+2 data
 */
export const fetchSkillTreeLevelOneTwo = async (skillName, resultId, model = "gpt") => {
  try {
    const response = await callSkillTreeWithRetry(() =>
      apis.POST({
        endpoint: "/skill-tree-level-one-two",
        payload: {
          skillName,
          resultId,
          model,
        },
        options: {
          type: "json",
        },
      })
    );

    return {
      statusCode: response.status,
      body: response.data?.body || response.data?.result || response.data,
    };
  } catch (error) {
    console.error("Error fetching Level 1+2 data:", error);
    throw new Error(
      error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load Level 1+2 data"
    );
  }
};

/**
 * Get Level 3+4 data for Level 2 items
 * @param {Array} level2Items - Array of Level 2 items from API 1 response
 * @param {string} skillName - Main skill name for reference
 * @returns {Promise} API response with Level 3+4 data
 */
// export const fetchSkillTreeLevelThreeFour = async (level2Items, skillName) => {
//   try {
//     const response = await apis.POST({
//       endpoint: "/skill-tree-level-three-four",
//       payload: {
//         level2Items,
//         skillName,
//       },
//       options: {
//         type: "json",
//       },
//     });

//     return {
//       statusCode: response.status,
//       body: response.data?.body || response.data?.result || response.data,
//     };
//   } catch (error) {
//     console.error("Error fetching Level 3+4 data:", error);
//     throw new Error(
//       error.response?.data?.body?.message ||
//       error.response?.data?.message ||
//       error.message ||
//       "Failed to load Level 3+4 data"
//     );
//   }
// };

/**
 * Get next level children for a specific node
 * @param {Object} node - Node object with id, skillName, and level
 * @param {string} resultId - MongoDB ObjectId from tech_relevance_result collection
 * @param {string} skillName - Main skill name
 * @param {string} model - AI model to use: "gpt", "gemini", or "claude" (default: "gpt")
 * @returns {Promise} API response with next level children
 */
export const fetchSkillTreeNextLevel = async (node, resultId, skillName, model = "gpt") => {
  try {
    const response = await callSkillTreeWithRetry(() =>
      apis.POST({
        endpoint: "/skill-tree-next-level",
        payload: {
          node: {
            id: node.id,
            skillName: node.skillName,
            level: node.level,
          },
          resultId,
          skillName,
          model,
        },
        options: {
          type: "json",
        },
      })
    );

    return {
      statusCode: response.status,
      body: response.data?.body || response.data?.result || response.data,
    };
  } catch (error) {
    console.error("Error fetching next level data:", error);
    throw new Error(
      error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load next level data"
    );
  }
};

/**
 * Save complete 4-level tree to database
 * @param {string} skillName - Main skill name
 * @param {string} resultId - MongoDB ObjectId from tech_relevance_result collection
 * @param {Array} latestTechnologies - Complete 4-level hierarchical structure
 * @param {number} percentageUsage - Usage percentage (0-100)
 * @returns {Promise} API response
 */
// export const saveSkillTreeComplete = async (
//   skillName,
//   resultId,
//   latestTechnologies,
//   percentageUsage
// ) => {
//   try {
//     const response = await apis.POST({
//       endpoint: "/save-skill-tree",
//       payload: {
//         skillName,
//         resultId,
//         latestTechnologies,
//         percentageUsage,
//       },
//       options: {
//         type: "json",
//       },
//     });

//     return {
//       statusCode: response.status,
//       body: response.data?.body || response.data?.result || response.data,
//     };
//   } catch (error) {
//     console.error("Error saving complete tree:", error);
//     throw new Error(
//       error.response?.data?.body?.message ||
//       error.response?.data?.message ||
//       error.message ||
//       "Failed to save tree data"
//     );
//   }
// };

/**
 * Get AI configuration including available models
 * @returns {Promise} API response with AI configuration
 */
export const fetchAIConfiguration = async () => {
  try {
    const response = await apis.GET({
      endpoint: "/get-ai-configuration",
    });

    return {
      statusCode: response.status,
      body: response.data?.body || response.data?.result || response.data,
    };
  } catch (error) {
    console.error("Error fetching AI configuration:", error);
    throw new Error(
      error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load AI configuration"
    );
  }
};